import {
  BaseBoxShapeUtil,
  HTMLContainer,
  Rectangle2d,
  TLBaseShape,
  TLResizeInfo,
  BaseBoxShapeTool
} from "tldraw"
import React from "react"

type SetSquareShape = TLBaseShape<
  "set-square",
  {
    w: number
    h: number
    flipped: boolean
  }
>

export class SetSquareShapeUtil extends BaseBoxShapeUtil<SetSquareShape> {
  static override type = "set-square" as const

  getDefaultProps(): SetSquareShape["props"] {
    return {
      w: 300,
      h: 300,
      flipped: false,
    }
  }

  getGeometry(shape: SetSquareShape) {
    return new Rectangle2d({
      width: shape.props.w,
      height: shape.props.h,
      isFilled: true,
    })
  }

  component(shape: SetSquareShape) {
    const { w, h, flipped } = shape.props

    const padding = 10

    // Triangle vertices depend on flip state
    // Normal:  right angle at bottom-left,  hypotenuse from bottom-right to top-left
    // Flipped: right angle at bottom-right, hypotenuse from bottom-left to top-right
    const rightX = flipped ? w - padding : padding   // right-angle corner X
    const rightY = h - padding                        // right-angle corner Y (always bottom)
    const baseX = flipped ? padding : w - padding     // other bottom corner X
    const baseY = h - padding                         // other bottom corner Y
    const topX = flipped ? w - padding : padding      // top corner X (same side as right angle)
    const topY = padding                              // top corner Y

    const baseLen = Math.abs(baseX - rightX)
    const sideLen = rightY - topY

    const pxPerCm = 50
    const pxPerMm = pxPerCm / 10
    const dir = flipped ? -1 : 1  // tick direction (inward)

    const ticks: React.ReactNode[] = []

    // Bottom edge ticks (along baseline)
    const bottomTotalMm = Math.floor(baseLen / pxPerMm)
    for (let i = 0; i <= bottomTotalMm; i++) {
      const x = rightX + (flipped ? -1 : 1) * i * pxPerMm
      const isCm = i % 10 === 0
      const isHalf = i % 5 === 0

      let tickH = 8
      if (isHalf) tickH = 12
      if (isCm) tickH = 18

      ticks.push(
        <line
          key={`b-${i}`}
          x1={x} y1={rightY}
          x2={x} y2={rightY - tickH}
          stroke="black" strokeWidth={1}
        />
      )

      if (isCm) {
        ticks.push(
          <text
            key={`bt-${i}`}
            x={x} y={rightY - 22}
            fontSize={11}
            textAnchor="middle"
            dominantBaseline="middle"
          >
            {i / 10}
          </text>
        )
      }
    }

    // Vertical edge ticks (along the side with the right angle)
    const sideTotalMm = Math.floor(sideLen / pxPerMm)
    for (let i = 0; i <= sideTotalMm; i++) {
      const y = rightY - i * pxPerMm
      const isCm = i % 10 === 0
      const isHalf = i % 5 === 0

      let tickH = 8
      if (isHalf) tickH = 12
      if (isCm) tickH = 18

      ticks.push(
        <line
          key={`l-${i}`}
          x1={rightX} y1={y}
          x2={rightX + dir * tickH} y2={y}
          stroke="black" strokeWidth={1}
        />
      )

      if (isCm) {
        ticks.push(
          <text
            key={`lt-${i}`}
            x={rightX + dir * 22} y={y}
            fontSize={11}
            textAnchor="middle"
            dominantBaseline="middle"
          >
            {i / 10}
          </text>
        )
      }
    }

    // Right angle indicator at the right-angle corner
    const arcR = 20
    ticks.push(
      <path
        key="right-angle"
        d={`M ${rightX + dir * arcR} ${rightY} L ${rightX + dir * arcR} ${rightY - arcR} L ${rightX} ${rightY - arcR}`}
        fill="none"
        stroke="#333"
        strokeWidth={1}
      />
    )

    // 45° marks at the other two corners
    const degArcR = 30

    // Bottom opposite corner (baseX, baseY): 45° arc
    const bArcStartX = baseX + (flipped ? 1 : -1) * degArcR
    const bArcEndX = baseX + (flipped ? 1 : -1) * degArcR * Math.cos(Math.PI / 4)
    const bArcEndY = baseY - degArcR * Math.sin(Math.PI / 4)
    const bSweep = flipped ? 0 : 1
    ticks.push(
      <path
        key="angle-base"
        d={`M ${bArcStartX} ${baseY} A ${degArcR} ${degArcR} 0 0 ${bSweep} ${bArcEndX} ${bArcEndY}`}
        fill="none"
        stroke="#333"
        strokeWidth={1}
      />
    )
    ticks.push(
      <text
        key="angle-base-text"
        x={baseX + (flipped ? 1 : -1) * 6}
        y={baseY - degArcR - 6}
        fontSize={10}
        textAnchor={flipped ? "start" : "end"}
      >
        45°
      </text>
    )

    // Top corner (topX, topY): 45° arc
    const tArcStartY = topY + degArcR
    const tArcEndX = topX + dir * degArcR * Math.sin(Math.PI / 4)
    const tArcEndY = topY + degArcR * Math.cos(Math.PI / 4)
    const tSweep = flipped ? 1 : 0
    ticks.push(
      <path
        key="angle-top"
        d={`M ${topX} ${tArcStartY} A ${degArcR} ${degArcR} 0 0 ${tSweep} ${tArcEndX} ${tArcEndY}`}
        fill="none"
        stroke="#333"
        strokeWidth={1}
      />
    )
    ticks.push(
      <text
        key="angle-top-text"
        x={topX + dir * (degArcR + 6)}
        y={topY + 14}
        fontSize={10}
        textAnchor="start"
      >
        45°
      </text>
    )

    return (
      <HTMLContainer>
        <svg width={w} height={h}>
          {/* Triangle fill */}
          <polygon
            points={`${rightX},${rightY} ${baseX},${baseY} ${topX},${topY}`}
            fill="rgba(246,230,180,0.3)"
            stroke="#333"
            strokeWidth={1}
          />

          {/* Ticks, labels, angle marks */}

          {ticks}

          {/* Double-headed flip arrow */}
          <g opacity={0.3}>
            <line x1={w / 2 - 12} y1={h / 2} x2={w / 2 + 12} y2={h / 2} stroke="#333" strokeWidth={1.5} />
            <path d={`M ${w / 2 - 12} ${h / 2} l 5 -4 m -5 4 l 5 4`} fill="none" stroke="#333" strokeWidth={1.5} />
            <path d={`M ${w / 2 + 12} ${h / 2} l -5 -4 m 5 4 l -5 4`} fill="none" stroke="#333" strokeWidth={1.5} />
          </g>
        </svg>
      </HTMLContainer>
    )
  }

  override onDoubleClick(shape: SetSquareShape) {
    return {
      id: shape.id,
      type: shape.type as "set-square",
      props: {
        flipped: !shape.props.flipped,
      },
    }
  }

  override onResize(shape: SetSquareShape, info: TLResizeInfo<SetSquareShape>) {
    const scale = Math.max(Math.abs(info.scaleX), Math.abs(info.scaleY))
    const size = Math.max(shape.props.w, shape.props.h) * scale
    return {
      props: {
        w: size,
        h: size,
      },
    }
  }

  indicator(shape: SetSquareShape) {
    return (
      <rect
        width={shape.props.w}
        height={shape.props.h}
      />
    )
  }
}

export const SetSquareIcon = () => (
  <svg width={20} height={20} viewBox="0 0 20 20">
    <polygon
      points="2,18 18,18 2,2"
      fill="none"
      stroke="#333"
      strokeWidth={1.5}
    />
    {/* Right angle indicator */}
    <path d="M 6,18 L 6,14 L 2,14" fill="none" stroke="#333" strokeWidth={1} />
  </svg>
)

export class SetSquareTool extends BaseBoxShapeTool {
  static override id = "set-square"
  static override initial = "idle"
  shapeType = "set-square"
}